import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Users, CheckCircle2, Loader2, Search } from "lucide-react";

/**
 * One-click "Bulk Qualify" button + dialog. Shown next to each service row in
 * Settings → Services. Lets owners/managers tick a checklist of every active
 * staff member who can perform that service and POST them in a single call to
 * /services/{id}/bulk-qualify. Existing qualifications are left untouched
 * (server-side upsert is idempotent).
 */
export function BulkQualifyButton({ service }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`bulk-qualify-btn-${service.service_id}`}
        className="h-8 px-3 text-xs font-['Manrope'] font-semibold rounded-full border-slate-300 hover:border-rose-400 hover:text-rose-600 hover:bg-rose-50"
      >
        <Users className="w-3.5 h-3.5 mr-1.5" /> Bulk Qualify
      </Button>
      {open && (
        <BulkQualifyDialog
          service={service}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function BulkQualifyDialog({ service, onClose }) {
  const [staff, setStaff] = useState([]);
  const [existingIds, setExistingIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [staffRes, qualRes] = await Promise.all([
          api.get("/staff"),
          api.get(`/services/${service.service_id}/qualified-staff`),
        ]);
        if (!alive) return;
        const eligibleRoles = ["stylist", "esthetician", "nail_tech", "colorist"];
        const active = (staffRes.data || []).filter(
          s => s.is_active !== false && eligibleRoles.includes(s.role)
        );
        const already = new Set((qualRes.data || []).map(q => q.user_id));
        setStaff(active);
        setExistingIds(already);
      } catch (err) {
        console.error("Bulk qualify load failed", err);
        toast.error("Couldn't load staff list");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [service.service_id]);

  const toggle = (uid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const newSelections = [...selectedIds].filter(id => !existingIds.has(id));

  const filtered = staff.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (s.name || "").toLowerCase().includes(q) || (s.role || "").toLowerCase().includes(q);
  });

  const submit = async () => {
    if (newSelections.length === 0) {
      toast.info("Tick at least one new staff member");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/services/${service.service_id}/bulk-qualify`, {
        user_ids: newSelections,
      });
      toast.success(`Qualified ${res.data?.qualified_count || newSelections.length} staff for ${service.name}`);
      onClose();
    } catch (err) {
      console.error("Bulk qualify failed", err);
      toast.error(err.response?.data?.detail || "Failed to qualify staff");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="bulk-qualify-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-rose-600" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <DialogTitle className="font-['Outfit'] text-xl tracking-tight">
                Qualify staff for {service.name}
              </DialogTitle>
              <DialogDescription className="text-xs font-['Manrope'] mt-1 leading-relaxed">
                Tick everyone who can perform this service. They'll be added to the booking pool with the standard ${service.price} price &middot; {service.duration}min duration. You can fine-tune custom prices later from each staff profile.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search staff by name or role"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm font-['Manrope'] rounded-full"
            data-testid="bulk-qualify-search"
          />
        </div>

        {loading ? (
          <div className="text-center py-8 text-sm text-slate-400 font-['Manrope']" data-testid="bulk-qualify-loading">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading staff…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-6 px-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-500 font-['Manrope']" data-testid="bulk-qualify-empty">
            {staff.length === 0 ? "No active stylists/estheticians found." : "No matches for that search."}
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="bulk-qualify-staff-list">
            {filtered.map(s => {
              const isAlready = existingIds.has(s.user_id);
              const isSelected = selectedIds.has(s.user_id);
              return (
                <button
                  key={s.user_id}
                  type="button"
                  onClick={() => !isAlready && toggle(s.user_id)}
                  disabled={isAlready}
                  data-testid={`bulk-qualify-row-${s.user_id}`}
                  className={`w-full flex items-center justify-between px-4 py-3 transition text-left
                    ${isAlready ? "bg-emerald-50/40 cursor-default" : "hover:bg-slate-50 cursor-pointer"}
                  `}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition
                      ${isAlready ? "bg-emerald-500 border-emerald-500" :
                        isSelected ? "bg-slate-900 border-slate-900" : "bg-white border-slate-300"}`}>
                      {(isAlready || isSelected) && <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate font-['Manrope']">{s.name}</p>
                      <p className="text-[11px] text-slate-400 capitalize">{(s.role || "").replace(/_/g, " ")}</p>
                    </div>
                  </div>
                  {isAlready && (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 font-['Manrope']">Already qualified</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate-400 font-['Manrope'] mr-auto" data-testid="bulk-qualify-count">
            {newSelections.length === 0 ? "No new staff selected" :
              `${newSelections.length} new staff to qualify`}
          </p>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="bulk-qualify-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || newSelections.length === 0}
            data-testid="bulk-qualify-submit"
            className="bg-rose-600 text-white rounded-full hover:bg-rose-700 disabled:opacity-50"
          >
            {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Saving…</> : `Qualify ${newSelections.length || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
